// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Encounter } from '@medplum/fhirtypes';
import { APPOINTMENT_TYPE_SYSTEM } from '../constants/appointmentTypes';
import type { AppointmentTypeDefinition } from '../types/appointmentTypes';

export const NEW_PATIENT_EM_CPTS = ['99202', '99203', '99204', '99205'] as const;
export const ESTABLISHED_EM_CPTS = ['99212', '99213', '99214', '99215'] as const;

export interface EmCptOptions {
  codes: readonly string[];
  defaultCode: string;
}

export function getEmCptOptions(
  appointmentType: Pick<AppointmentTypeDefinition, 'billingCode' | 'estimateCode'>
): EmCptOptions | undefined {
  const isNewPatient =
    appointmentType.estimateCode === 'intake' ||
    NEW_PATIENT_EM_CPTS.some((code) => code === appointmentType.billingCode);
  const isEstablished =
    appointmentType.estimateCode === 'check-in' ||
    appointmentType.estimateCode === 'follow-up' ||
    ESTABLISHED_EM_CPTS.some((code) => code === appointmentType.billingCode);

  if (!isNewPatient && !isEstablished) {
    return undefined;
  }

  const codes = isNewPatient ? NEW_PATIENT_EM_CPTS : ESTABLISHED_EM_CPTS;
  return {
    codes,
    defaultCode: codes.some((code) => code === appointmentType.billingCode) ? appointmentType.billingCode : codes[0],
  };
}

export function getEncounterEmCptOptions(
  encounter: Encounter,
  appointmentTypes: AppointmentTypeDefinition[]
): EmCptOptions | undefined {
  const appointmentTypeCode = encounter.type?.[0]?.coding?.find(
    (coding) => coding.system === APPOINTMENT_TYPE_SYSTEM
  )?.code;
  const appointmentType = appointmentTypes.find((type) => type.coding.code === appointmentTypeCode);
  return appointmentType ? getEmCptOptions(appointmentType) : undefined;
}
